// v3.3 TALENT TREE: persistent metagame progression.
// Perks are permanent player buffs earned by spending PERK POINTS at the perk shrine.
// Perk points are awarded +1 per boss killed. Applied at the start of every run via applyPerks().

export const PERKS = {
  // ------- AGGRESSION branch (6 perks, tiered prereqs) -------
  aggression_start_str: {
    id: 'aggression_start_str', name: 'FIRST BLOOD', tier: 1, branch: 'aggression', requires: null,
    art: '⚔', color: '#ff2b2b',
    desc: 'Start every combat with +1 STRENGTH.',
    apply: (player) => { player._perkStartStr = (player._perkStartStr || 0) + 1; },
  },
  aggression_first_attack_free: {
    id: 'aggression_first_attack_free', name: 'PREEMPTIVE STRIKE', tier: 2, branch: 'aggression', requires: 'aggression_start_str',
    art: '🎯', color: '#ff2b2b',
    desc: 'The first attack card you play each combat costs 0.',
    apply: (player) => { player._perkFirstAttackFree = true; },
  },
  aggression_max_energy: {
    id: 'aggression_max_energy', name: 'OVERCLOCK', tier: 3, branch: 'aggression', requires: 'aggression_first_attack_free',
    art: '⚡', color: '#ff2b2b',
    desc: '+1 MAX ENERGY every turn.',
    apply: (player) => { player.maxEnergy = (player.maxEnergy || 3) + 1; },
  },
  aggression_rare_start: {
    id: 'aggression_rare_start', name: 'HEIRLOOM', tier: 4, branch: 'aggression', requires: 'aggression_max_energy',
    art: '🏆', color: '#ff2b2b',
    desc: 'Start each run with a random RARE card in your deck.',
    apply: (player) => { player._perkRareStarter = true; },
  },
  aggression_crit_chance: {
    id: 'aggression_crit_chance', name: 'GLASS CANNON', tier: 5, branch: 'aggression', requires: 'aggression_rare_start',
    art: '💥', color: '#ff2b2b',
    desc: 'Attacks deal +25% damage. -20% max HP.',
    apply: (player) => { player._perkDmgMult = (player._perkDmgMult || 1) * 1.25; player.maxHp = Math.floor(player.maxHp * 0.8); player.hp = Math.min(player.hp, player.maxHp); },
  },
  aggression_execute: {
    id: 'aggression_execute', name: 'EXECUTIONER', tier: 6, branch: 'aggression', requires: 'aggression_crit_chance',
    art: '☠', color: '#ff2b2b',
    desc: 'Attacks that would leave the enemy at ≤5 HP kill instantly. Elites unaffected.',
    apply: (player) => { player._perkExecute = 5; },
  },
  // ------- SUSTAIN branch (6 perks) -------
  sustain_max_hp: {
    id: 'sustain_max_hp', name: 'THICK SKIN', tier: 1, branch: 'sustain', requires: null,
    art: '💪', color: '#39ff14',
    desc: '+10 MAX HP.',
    apply: (player) => { player.maxHp += 10; player.hp += 10; },
  },
  sustain_retain_on_rest: {
    id: 'sustain_retain_on_rest', name: 'MEMORY FOAM', tier: 2, branch: 'sustain', requires: 'sustain_max_hp',
    art: '🧠', color: '#39ff14',
    desc: 'Enter every combat with +5 BLOCK.',
    apply: (player) => { player._perkStartBlock = (player._perkStartBlock || 0) + 5; },
  },
  sustain_rest_heal_bonus: {
    id: 'sustain_rest_heal_bonus', name: 'DEEP SLEEP', tier: 3, branch: 'sustain', requires: 'sustain_retain_on_rest',
    art: '💤', color: '#39ff14',
    desc: 'REST heals +10 HP.',
    apply: (player) => { player._perkRestBonus = 10; },
  },
  sustain_regen: {
    id: 'sustain_regen', name: 'REGEN LOOP', tier: 4, branch: 'sustain', requires: 'sustain_rest_heal_bonus',
    art: '♻', color: '#39ff14',
    desc: 'Heal 2 HP at the start of every combat.',
    apply: (player) => { player._perkCombatStartHeal = (player._perkCombatStartHeal || 0) + 2; },
  },
  sustain_lifesteal: {
    id: 'sustain_lifesteal', name: 'BLOOD PACT', tier: 5, branch: 'sustain', requires: 'sustain_regen',
    art: '🩸', color: '#39ff14',
    desc: 'Heal 1 HP every time you kill an enemy.',
    apply: (player) => { player._perkKillHeal = (player._perkKillHeal || 0) + 1; },
  },
  sustain_immortal: {
    id: 'sustain_immortal', name: 'IMMORTAL CORE', tier: 6, branch: 'sustain', requires: 'sustain_lifesteal',
    art: '🛡', color: '#39ff14',
    desc: 'Once per run, survive lethal damage at 1 HP.',
    apply: (player) => { player._perkImmortalAvail = true; },
  },
  // ------- UTILITY branch (6 perks) -------
  utility_start_gold: {
    id: 'utility_start_gold', name: 'TRUST FUND', tier: 1, branch: 'utility', requires: null,
    art: '💰', color: '#ffd700',
    desc: 'Start each run with +50 GOLD.',
    apply: (player) => { player.gold = (player.gold || 0) + 50; },
  },
  utility_extra_reward: {
    id: 'utility_extra_reward', name: 'LOOT GOBLIN', tier: 2, branch: 'utility', requires: 'utility_start_gold',
    art: '🎁', color: '#ffd700',
    desc: '+1 card choice on every reward.',
    apply: (player) => { player.mods = { ...(player.mods || {}), rewardCount: (player.mods?.rewardCount ?? 3) + 1 }; },
  },
  utility_shop_discount: {
    id: 'utility_shop_discount', name: 'HAGGLER', tier: 3, branch: 'utility', requires: 'utility_extra_reward',
    art: '🏷', color: '#ffd700',
    desc: 'All shop prices reduced by 20%.',
    apply: (player) => { player.mods = { ...(player.mods || {}), shopPriceMult: (player.mods?.shopPriceMult ?? 1) * 0.8 }; },
  },
  utility_draw_bonus: {
    id: 'utility_draw_bonus', name: 'GALAXY BRAIN', tier: 4, branch: 'utility', requires: 'utility_shop_discount',
    art: '🌌', color: '#ffd700',
    desc: 'Draw +1 card each turn.',
    apply: (player) => { player._perkExtraDrawEach = (player._perkExtraDrawEach || 0) + 1; },
  },
  utility_divine_odds: {
    id: 'utility_divine_odds', name: 'DIVINE INSIGHT', tier: 5, branch: 'utility', requires: 'utility_draw_bonus',
    art: '👁', color: '#ffd700',
    desc: 'Divine drop chance +100% (from ~4% to ~8%).',
    apply: (player) => { player._perkDivineBoost = true; },
  },
  utility_prestige: {
    id: 'utility_prestige', name: 'GALAXY GOOMER', tier: 6, branch: 'utility', requires: 'utility_divine_odds',
    art: '🌟', color: '#ffd700',
    desc: '+25% slop tokens earned.',
    apply: (player) => { player._perkTokenMult = (player._perkTokenMult || 1) * 1.25; },
  },
};

export const PERK_LIST = Object.values(PERKS);
export const PERK_BRANCHES = ['aggression', 'sustain', 'utility'];

// v3.6 LOADOUT PRESETS: multi-perk one-click builds. Perks are unlocked in order — prereqs are respected
// because each preset only chains within a single branch (or a hybrid that walks tier-by-tier).
export const LOADOUT_PRESETS = [
  {
    id: 'bruiser',
    name: 'BRUISER',
    hue: '#ff2b2b',
    tagline: 'PUNCH FIRST. THINK NEVER.',
    desc: 'Front-load damage: start each combat with STR, free first attack, and extra energy.',
    perkIds: ['aggression_start_str', 'aggression_first_attack_free', 'aggression_max_energy'],
  },
  {
    id: 'economist',
    name: 'ECONOMIST',
    hue: '#ffd700',
    tagline: 'GET RICH OR RESPEC TRYING.',
    desc: 'Fatter starting gold, more rewards, cheaper shops. Big brain scaling.',
    perkIds: ['utility_start_gold', 'utility_extra_reward', 'utility_shop_discount'],
  },
  {
    id: 'immortal',
    name: 'IMMORTAL',
    hue: '#39ff14',
    tagline: 'HP IS A LIFESTYLE.',
    desc: 'Bigger HP pool, block on entry, and rest heals more. Tank everything.',
    perkIds: ['sustain_max_hp', 'sustain_retain_on_rest', 'sustain_rest_heal_bonus'],
  },
  {
    id: 'glass_cannon',
    name: 'GLASS CANNON',
    hue: '#ff007f',
    tagline: 'EXTENDS BRUISER · ALL DAMAGE. NO REGRETS.',
    desc: 'The full AGGRESSION path to tier 5 — for players who WANT their 20% max HP gone.',
    perkIds: [
      'aggression_start_str', 'aggression_first_attack_free', 'aggression_max_energy',
      'aggression_rare_start', 'aggression_crit_chance',
    ],
  },
];

export function perkById(id) { return PERKS[id] || null; }

// Apply all owned perks to a fresh player. Called at run start after newPlayer() constructs the base player.
export function applyPerks(player, ownedPerkIds = []) {
  for (const id of ownedPerkIds) {
    const p = PERKS[id];
    if (p && typeof p.apply === 'function') {
      try { p.apply(player); } catch (e) { /* silent */ }
    }
  }
  return player;
}
